import React from 'react';
import { useTranslation } from 'react-i18next';
import * as ResultModule from 'modules/result';
import { useStudyPlan } from 'modules/studyPlan';
import { categoryTheme } from '../categoryThemes';
import { SkillCards } from './SkillCards';
import { summarizeIelts, summarizeSkills, weakestTopics } from './ieltsSummary';

const CHART_W = 320;
const CHART_H = 150;
const PAD_L = 28;
const PAD_T = 22;
const PAD_B = 14;

/** Bands as a line on a fixed scale that always includes the target, so the gap to it is visible. */
function BandTrend({ bands, target, accent }: { bands: number[]; target: number | null; accent: string }) {
  const { t, i18n } = useTranslation();
  const lo = Math.floor(Math.min(...bands, target ?? Infinity) - 0.5);
  const hi = Math.ceil(Math.max(...bands, target ?? -Infinity) + 0.5);
  const y = (band: number) => PAD_T + ((hi - band) / (hi - lo)) * (CHART_H - PAD_T - PAD_B);
  const x = (index: number) => PAD_L + (bands.length === 1 ? 0.5 : index / (bands.length - 1)) * (CHART_W - PAD_L - 20);
  const fmt = (band: number) => band.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const ticks = [lo + 1, Math.round((lo + hi) / 2), hi - 1].filter((tick, i, all) => all.indexOf(tick) === i);

  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      width="100%"
      role="img"
      aria-label={t('portal.ielts_trend_aria', { bands: bands.map(fmt).join(', '), target: target ? fmt(target) : '—' })}
      className="mt-3 block"
    >
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={PAD_L} x2={CHART_W - 8} y1={y(tick)} y2={y(tick)} stroke="hsl(var(--border))" />
          <text x={2} y={y(tick) + 3} fontSize={10} fill="hsl(var(--muted-foreground))">{tick.toFixed(1)}</text>
        </g>
      ))}
      {target !== null && (
        <g>
          <line x1={PAD_L} x2={CHART_W - 8} y1={y(target)} y2={y(target)} stroke={accent} strokeDasharray="4 4" />
          <text x={PAD_L + 2} y={y(target) - 5} fontSize={10} fontWeight={700} fill={accent}>{t('portal.ielts_trend_target', { target: fmt(target) })}</text>
        </g>
      )}
      <polyline
        points={bands.map((band, i) => `${x(i)},${y(band)}`).join(' ')}
        fill="none"
        stroke="hsl(var(--foreground))"
        strokeWidth={2.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {bands.map((band, i) => {
        const last = i === bands.length - 1;
        return (
          <circle
            key={i}
            cx={x(i)}
            cy={y(band)}
            r={last ? 6 : 4.5}
            fill={last ? 'hsl(var(--primary))' : 'hsl(var(--card))'}
            stroke="hsl(var(--foreground))"
            strokeWidth={2}
          />
        );
      })}
    </svg>
  );
}

/**
 * Under the IELTS tests: the learner's own numbers (goal, best band, attempts, last attempt), their band over
 * time, and the question topics they get wrong most. All of it comes from their results and study plan; with
 * neither, nothing shows.
 */
export function IeltsPanel() {
  const { t, i18n } = useTranslation();
  const { accent } = categoryTheme('ielts');
  const { items } = ResultModule.Hooks.useList({ params: { perPage: 50 } });
  const { data: plan } = useStudyPlan();
  const goal = plan?.goals?.find((g) => g.exam === 'IELTS');
  const summary = summarizeIelts(items, goal?.level);
  const weak = weakestTopics(goal?.mastery);
  const skills = summarizeSkills(items);

  if (summary.attempts === 0 && summary.target === null && weak.length === 0) return null;

  const { bands, target, best, lastAt, attempts } = summary;
  const fmt = (band: number) => band.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const growth = bands.length > 1 ? bands[bands.length - 1] - bands[0] : null;
  const gap = target !== null && bands.length ? Math.max(0, target - bands[bands.length - 1]) : null;
  const facts: Array<{ label: string; value: string }> = [
    target !== null && { label: t('portal.ielts_fact_target'), value: fmt(target) },
    best !== null && { label: t('portal.ielts_fact_best'), value: fmt(best) },
    attempts > 0 && { label: t('portal.ielts_fact_attempts'), value: String(attempts) },
    lastAt && { label: t('portal.ielts_fact_last'), value: lastAt.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }) },
  ].filter((fact): fact is { label: string; value: string } => Boolean(fact));

  return (
    <section aria-label={t('portal.ielts_panel')} className="mt-8 flex flex-col gap-4 md:mt-10">
      {facts.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {facts.map((fact) => (
            <li key={fact.label} className="rounded-[11px] border border-border bg-card px-3.5 py-2 text-[13px] font-semibold text-foreground">
              {fact.label} <b style={{ color: accent }}>{fact.value}</b>
            </li>
          ))}
        </ul>
      )}
      {skills.some((skill) => skill.attempts > 0) && <SkillCards skills={skills} target={target} accent={accent} />}
      <div className="grid gap-4 md:grid-cols-2 md:gap-5">
        {bands.length > 0 && (
          <section aria-labelledby="ielts-trend" className="rounded-[20px] border border-border bg-card p-5 md:p-[22px]">
            <h2 id="ielts-trend" className="font-display text-[17px] font-extrabold tracking-[-0.03em] text-foreground">{t('portal.ielts_trend_title')}</h2>
            <BandTrend bands={bands} target={target} accent={accent} />
            {(growth !== null || gap !== null) && (
              <p className="mt-2 text-[12.5px] text-muted-foreground">
                {growth !== null && growth > 0 && `${t('portal.ielts_trend_growth', { count: bands.length, delta: `+${growth.toFixed(1)}` })} `}
                {gap !== null && gap > 0 && t('portal.ielts_trend_remaining', { gap: gap.toFixed(1) })}
              </p>
            )}
          </section>
        )}
        {weak.length > 0 && (
          <section aria-labelledby="ielts-weak" className="rounded-[20px] border border-border bg-card p-5 md:p-[22px]">
            <h2 id="ielts-weak" className="font-display text-[17px] font-extrabold tracking-[-0.03em] text-foreground">{t('portal.ielts_weak_title')}</h2>
            <p className="mb-1 mt-1 text-[12.5px] text-muted-foreground">{t('portal.ielts_weak_hint')}</p>
            <ul>
              {weak.map((topic) => (
                <li key={topic.topic} className="flex flex-col gap-1.5 border-t border-border py-3 first:border-t-0">
                  <div className="flex justify-between gap-3 text-[13px] font-semibold text-foreground">
                    <span className="min-w-0 truncate">{topic.topic}</span>
                    <span className="tabular-nums">{topic.mastery}%</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-border" role="img" aria-label={`${topic.topic}: ${topic.mastery}%`}>
                    <div className="h-full rounded-full" style={{ width: `${topic.mastery}%`, background: accent }} />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </section>
  );
}

export default IeltsPanel;
